import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshCardStyles, getRefreshPaperStyles } from '../utils/refreshAnimation';
import { healthCheckService } from '../services/healthCheckService';
import { applicationDefinitionService } from '../services/applicationDefinitionService';
import { agentService } from '../services/agentService';
import { formatDateToTR } from '../utils/dateUtils';
import type { HealthCheck, CurrentHealthStatus } from '../types/healthCheck';
import type { Agent } from '../types/agent';
import { HealthStatus } from '../types/healthCheck';
import type { ApplicationDefinition } from '../types/applicationDefinition';

const HealthCheckHistory = () => {
  const { id } = useParams<{ id: string }>();
  const { refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const navigate = useNavigate();
  const [applicationDefinition, setApplicationDefinition] = useState<ApplicationDefinition | null>(null);
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [currentStatus, setCurrentStatus] = useState<CurrentHealthStatus | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Paging state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Details modal state
  const [selectedDetails, setSelectedDetails] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [appDef, historyResult, status] = await Promise.all([
        applicationDefinitionService.getById(id),
        healthCheckService.getHistory(id, page + 1, pageSize),
        healthCheckService.getCurrentStatus(id),
      ]);

      // Agent'ları yükle (agent durumu için gerekli)
      const allAgents = await agentService.getAll();
      setAgents(allAgents);

      console.log('[HealthCheckHistory] Data loaded:', {
        appDef,
        historyResult,
        status,
        statusLatestHealthStatus: status?.latestHealthStatus,
        statusLatestDetails: status?.latestDetails,
      });

      setApplicationDefinition(appDef);
      setHealthChecks(historyResult.items);
      setTotalCount(historyResult.totalCount);
      setCurrentStatus(status);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [id, page, pageSize]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh || !id) return;

    const interval = setInterval(() => {
      loadData();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, id, loadData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (details: string) => {
    setSelectedDetails(details);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedDetails(null);
  };

  const formatJsonDetails = (details: string): string => {
    try {
      const parsed = JSON.parse(details);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return details;
    }
  };

  const getHealthStatusColor = (status: HealthStatus, appDef?: ApplicationDefinition): 'success' | 'error' | 'warning' => {
    // Önce agent durumunu kontrol et - eğer agent offline ise kritik
    if (appDef) {
      const serverAgents = agents.filter(a => a.serverId === appDef.serverId);
      const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
      
      if (!hasOnlineAgent && serverAgents.length > 0) {
        // Server'ın agent'ı var ama offline - kritik
        return 'error';
      }
    }

    // Agent durumu iyi ise health check'e göre değerlendir
    switch (status) {
      case HealthStatus.Healthy:
        return 'success';
      case HealthStatus.Unhealthy:
        return 'error';
      case HealthStatus.Unknown:
        return 'warning';
      default:
        return 'warning';
    }
  };

  const getHealthStatusLabel = (status: HealthStatus, appDef?: ApplicationDefinition): string => {
    // Önce agent durumunu kontrol et - eğer agent offline ise kritik
    if (appDef) {
      const serverAgents = agents.filter(a => a.serverId === appDef.serverId);
      const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
      
      if (!hasOnlineAgent && serverAgents.length > 0) {
        // Server'ın agent'ı var ama offline - kritik
        return 'Kritik (Erişim Yok)';
      }
    }

    // Agent durumu iyi ise health check'e göre değerlendir
    switch (status) {
      case HealthStatus.Healthy:
        return 'Sağlıklı';
      case HealthStatus.Unhealthy:
        return 'Sağlıksız';
      case HealthStatus.Unknown:
        return 'Bilinmiyor';
      default:
        return 'Bilinmiyor';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!applicationDefinition) {
    return (
      <Layout>
        <Alert severity="error">Uygulama tanımı bulunamadı</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/application-definitions')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Uygulama Sağlığı Geçmişi: {applicationDefinition.name}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {currentStatus && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' }, minWidth: { xs: '100%', md: '200px' } }}>
            <Card sx={getRefreshCardStyles(isRefreshing)}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Mevcut Durum
                </Typography>
                <Typography variant="h6">
                  <Chip
                    label={getHealthStatusLabel(currentStatus.latestHealthStatus)}
                    color={getHealthStatusColor(currentStatus.latestHealthStatus)}
                    size="small"
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {currentStatus.latestCheckTime
                    ? formatDateToTR(currentStatus.latestCheckTime)
                    : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' }, minWidth: { xs: '100%', md: '200px' } }}>
            <Card sx={getRefreshCardStyles(isRefreshing)}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Son 24 Saat - Sağlıklı
                </Typography>
                <Typography variant="h6" color="success.main">
                  {currentStatus.last24HoursHealthyCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Toplam: {currentStatus.totalChecksLast24Hours}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' }, minWidth: { xs: '100%', md: '200px' } }}>
            <Card sx={getRefreshCardStyles(isRefreshing)}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Son 24 Saat - Sağlıksız
                </Typography>
                <Typography variant="h6" color="error.main">
                  {currentStatus.last24HoursUnhealthyCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Toplam: {currentStatus.totalChecksLast24Hours}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' }, minWidth: { xs: '100%', md: '200px' } }}>
            <Card sx={getRefreshCardStyles(isRefreshing)}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Son 24 Saat - Bilinmiyor
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {currentStatus.last24HoursUnknownCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Toplam: {currentStatus.totalChecksLast24Hours}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      <Paper sx={{ borderRadius: 3, boxShadow: '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Kontrol Zamanı</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Durum</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Agent Versiyonu</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Detaylar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {healthChecks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Henüz health check kaydı bulunmuyor.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                healthChecks.map((check, index) => (
                  <TableRow
                    key={check.id}
                    hover
                    sx={{
                      bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      {formatDateToTR(check.checkTime)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Chip
                        label={getHealthStatusLabel(check.healthStatus, applicationDefinition)}
                        color={getHealthStatusColor(check.healthStatus, applicationDefinition)}
                        size="small"
                        sx={{ height: 22, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>v{check.agentVersion}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      {check.details ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem',
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {check.details.length > 50
                              ? `${check.details.substring(0, 50)}...`
                              : check.details}
                          </Typography>
                          <Tooltip title="Detayları Görüntüle">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(check.details!)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangePageSize}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Sayfa başına kayıt:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `~${to}`}`}
        />
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detaylar</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100],
              borderRadius: 1,
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {selectedDetails ? formatJsonDetails(selectedDetails) : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsModal}>Kapat</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default HealthCheckHistory;

